import { useTranslation } from 'react-i18next';
import { Navigate, useParams } from 'react-router-dom';

import { Mail } from 'lucide-react';

import { Card, PageHeader } from '../../components';

/**
 * 마이페이지의 문서 화면 — 약관·방침·공지·FAQ·문의·라이선스(`/more/docs/:id`).
 *
 * 본문은 i18n의 **자리표시자 문구**다(`web.more.docs.*.body`). 한 컴포넌트가 여섯 문서를
 * 그리는 이유: 문서마다 화면을 만들면 여섯 벌의 레이아웃이 생기고, 정식 문구가 들어오는
 * 날 여섯 곳을 고친다. 문단은 빈 줄로 가르고, `Q.`로 시작하는 줄과 `[…]`로 시작하는 줄은
 * 제목처럼 굵게 찍는다 — FAQ·공지가 그 관례를 쓴다.
 *
 * 문의 화면에는 메일 버튼이 하나 더 선다(`mailto:`). 주소는 본문의 것과 같아야 한다.
 */

export const DOC_IDS = [
  'terms',
  'privacy',
  'notice',
  'faq',
  'contact',
  'licenses',
] as const;
export type DocId = (typeof DOC_IDS)[number];

const SUPPORT_EMAIL = 'support@muzly.io';

const isDocId = (value: string | undefined): value is DocId =>
  DOC_IDS.some((id) => id === value);

const isHeading = (line: string): boolean =>
  /^(Q\.|\[|제\d+조|Article \d+|\d+\. )/.test(line);

export const DocPage = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();

  if (!isDocId(id)) {
    return <Navigate to="/more" replace />;
  }

  const paragraphs = t(`web.more.docs.${id}.body`).split('\n\n');

  return (
    <div className="flex min-h-dvh flex-col bg-background pb-10 pt-[calc(var(--safe-area-top,0px)+0.25rem)]">
      <PageHeader title={t(`web.more.docs.${id}.title`)} />
      <div className="px-[18px] pt-2">
        <Card className="px-5 py-6">
          <div className="flex flex-col gap-5">
            {paragraphs.map((paragraph, i) => {
              const [first, ...rest] = paragraph.split('\n');
              const heading = isHeading(first);
              return (
                <div key={i} className="flex flex-col gap-1.5">
                  <p
                    className={
                      heading
                        ? 'text-[15px] font-semibold text-bk-900'
                        : 'text-sm leading-relaxed text-bk-800'
                    }
                  >
                    {first}
                  </p>
                  {rest.length > 0 && (
                    <p className="whitespace-pre-line text-sm leading-relaxed text-bk-800">
                      {rest.join('\n')}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </Card>

        {id === 'contact' && (
          <a
            href={`mailto:${SUPPORT_EMAIL}`}
            className="mt-6 flex h-[54px] w-full items-center justify-center gap-2 rounded-full bg-black text-base font-bold text-white"
          >
            <Mail className="h-5 w-5" aria-hidden="true" />
            {t('web.more.contactCta')}
          </a>
        )}
      </div>
    </div>
  );
};
